import _ from 'lodash'
import React from 'react'
import { Link } from 'react-router'

import {
  ADMINISTRATION_STATUSES,
  ADMINISTRATION_STATUS_LABELS,
  ADMINISTRATION_TYPE_LABELS,
} from 'services/administrations/constants'
import { Administration, AdministrationType, EmergencyDose } from 'services/administrations/models'
import { FREQUENCIES, FREQUENCY_LABELS, MF_SIGNATURE_TYPES } from 'services/medicationForms/constants'

import { generateRoute } from 'utils/routing'
import { createTypeFromEnumValues } from 'utils/typescript'

import { Avatar, Button, DateString, DropdownMenu, TimeString, Typography } from 'components'

import i18n from 'translations'

type FinalAdministration = Administration & EmergencyDose

const getFrequency = (
  frequency: createTypeFromEnumValues<FREQUENCIES>,
  dose: string,
  type: AdministrationType,
) => {
  if ('emergency' === type) {
    return <Typography width="80px" ellipsis>{dose}</Typography>
  }

  if (!frequency) {
    return null
  }

  if (frequency === FREQUENCIES.DAILY) {
    return (
      <React.Fragment>
        {FREQUENCY_LABELS[frequency]}
        <br />
        <Typography width="80px" ellipsis>{dose}</Typography>
      </React.Fragment>
    )
  }

  return FREQUENCY_LABELS[frequency]
}

export const getTableData = (
  administrationList: (FinalAdministration)[],
  childId: number,
  onParentSignatureClick: (id: number) => void,
  onExportClick: (id: number) => void,
) => {
  if (!administrationList?.length) {
    return null
  }

  return _.map(administrationList, (item) => {
    const {
      child: rootChild,
      createdAt,
      id,
      medicationDetails: rootMedicationDetails,
      medicationForm,
      signatures,
      status,
      takenAt,
      timeOfAdministration,
      type,
    } = item
    const { child: medicationFormChild, id: medicationFormId, medicationDetails } = medicationForm || {}
    const finalMedicationDetails = rootMedicationDetails || medicationDetails || {}

    const { dose, frequency, medication } = finalMedicationDetails
    const administeredSign = _.find(signatures, { type: MF_SIGNATURE_TYPES.STAFF })

    const finalChild = rootChild || medicationFormChild
    const finalMedication = rootMedicationDetails?.medication || medication

    return {
      actions: (
        <DropdownMenu minWidth={280} small>
          {medicationFormId && (
            <DropdownMenu.Item
              icon="medication"
              label={i18n.t('module:Safeguarding:MedicationForm:Common:medicationForm')}
              to={childId
                ? generateRoute('CHILDREN.CHILD.SAFEGUARDING.MEDICATION_FORM.PREVIEW', {
                  childId, id: medicationFormId,
                })
                : generateRoute('SAFEGUARDING.MEDICATION_FORM.PREVIEW', { id: medicationFormId })}
            />
          )}
          <DropdownMenu.Item
            icon="administration"
            label={i18n.t('module:Safeguarding:MedicationForm:Common:administrationDetails')}
            to={childId
              ? generateRoute('CHILDREN.CHILD.SAFEGUARDING.ADMINISTRATION.PREVIEW', { childId, id })
              : generateRoute('SAFEGUARDING.ADMINISTRATION.PREVIEW', { id })}
          />
          <DropdownMenu.Item
            icon="export"
            label={i18n.t('global:Export')}
            onClick={() => onExportClick(id)}
          />
        </DropdownMenu>
      ),
      administeredBy: (
        <Link to={generateRoute('STAFF.PROFILE.ABOUT', { userId: administeredSign?.owner?.id })}>
          {administeredSign?.owner?.name}
        </Link>
      ),
      child: finalChild ? (
        <Avatar
          avatarSize="small"
          initials={[finalChild.firstName, finalChild.surname]}
          minWidth={150}
          src={finalChild.photo}
          title={(
            <Typography fontSize={16} primary>
              {`${finalChild.firstName} ${finalChild.surname}`}
            </Typography>
          )}
          to={generateRoute('CHILDREN.CHILD.ABOUT', { childId: finalChild.id })}
        />
      ) : undefined,
      createdAt: <DateString date={createdAt as Date} />,
      frequency: getFrequency(frequency, dose, type),
      medication: (
        <Typography primary={!!medicationFormId} width="120px" ellipsis>
          {medicationFormId ? (
            <Link to={childId
              ? generateRoute('CHILDREN.CHILD.SAFEGUARDING.MEDICATION_FORM.PREVIEW', { childId, id: medicationFormId })
              : generateRoute('SAFEGUARDING.MEDICATION_FORM.PREVIEW', { id: medicationFormId })}
            >
              {finalMedication}
            </Link>
          ) : finalMedication}
        </Typography>
      ),
      parentSignatureAction: (
        status === ADMINISTRATION_STATUSES.ADMINISTERED && (
          <Button
            hierarchy="secondary"
            label={i18n.t('module:Safeguarding:Administration:List:Table:parentSignatureButtonLabel')}
            size="small"
            onClick={() => onParentSignatureClick(id)}
          />
        )
      ),
      status: ADMINISTRATION_STATUS_LABELS[status],
      timeOfAdministration: timeOfAdministration
        ? <TimeString date={timeOfAdministration} local={false} />
        : <TimeString date={takenAt as Date} />,
      type: ADMINISTRATION_TYPE_LABELS[type],
    }
  })
}
